import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
// ## //
import widgetController from "../api/widgetSlice"
import { checkUserType } from "../component/userUtils"
import { CancelToken } from "../api/httpClient"
import { STAFF } from "../commons/userTypes"

const { actions } = widgetController

function genericEndpointAPI(widgetID) {
  return `/widgets/generic/${widgetID}`
}

/**
 * React hooks style HOC that will add widgetData to your component.
 * TODO : faire evoluer pour gerer une liste de widgetID avec param
 */
function useWidgetData({
  widgetId,
  subscriptions,
  reqOpt = {},
  disableDispatch = false
}) {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const widgetPreview = useSelector((state) => state.widgets.previewEnabled)
  const locale = useSelector((state) => state.intl.locale)
  const widgetData = useSelector((state) => state.widgets.widgetData)

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(undefined)
  const [error, setError] = useState(undefined)

  // We get tempParams as we need to do a deepEqual and
  const subscriptionsParams = useSelector((state) => {
    if (subscriptions) {
      return _.mapValues(subscriptions, (k) => {
        if (k === "current_user") {
          if (typeof state.auth.user.jumpId === "undefined") {
            // eslint-disable-next-line no-console
            setError(
              "jumpId is undefined, user is an admin or there is no account where current user is referred as main contact"
            )
          }
          return [state.auth.user.jumpId]
        }

        return [state.widgets.selector[k]?.selected]
      })
    }
    return {}
  })

  const { endpoint, bodyOpt } = reqOpt
  const realBodyOpt = bodyOpt ?? {}
  const realEndpoint = endpoint ?? genericEndpointAPI(widgetId)

  // this is a common pattern to be sure useEffect vector
  // is comparing depth value, rather object references
  const realBodyOptStr = JSON.stringify(realBodyOpt)
  const subscriptionsParamsStr = JSON.stringify(subscriptionsParams)

  useEffect(() => {
    if (widgetData && Object.entries(widgetData).length > 0) {
      setData(widgetData)
      setError(undefined)
      setLoading(false)
    } else {
      setError("no data")
    }
  }, [widgetData])

  useEffect(() => {
    // useWidgetData can be used as "public" widget without any user authenticated.
    // but when it is used in a "private" widget, and when a user is authenticated
    // as staff member, then by pass control
    const isUserStaffOrUnauthenticated =
      _.isUndefined(user) || checkUserType(user, STAFF)

    if (widgetPreview) {
      return () => {
        // basically a noop function, to be consistent with unmount function
      }
    }

    // when subscriptions are set, it means that it is dependant of selector values,
    // so when it has subscriptions, whereas those values are still undefined,
    // then for optimization avoid to call API
    const hasUndefinedParams =
      _(subscriptionsParams)
        .values()
        .filter(_.isArray)
        .flatten()
        .filter(_.isUndefined)
        .value().length > 0
    if (hasUndefinedParams && !isUserStaffOrUnauthenticated) {
      // console.log(
      //   "[useWidgetData] skip fetch => undefined params for non admin users"
      // )
      setError("undefined params for non admin users")
      setLoading(false)
      return () => {
        // basically a noop function, to be consistent with unmount function
      }
    }

    const { token, cancel: cancelRequest } = CancelToken.source() // Request cancellation token
    const args = {
      token,
      key: widgetId,
      subscriptionsParams: {
        ...subscriptionsParams,
        ...realBodyOpt,
        language: locale
      },
      endpoint: realEndpoint,
      disableDispatch
    }

    setLoading(true)
    dispatch(actions.fetchWidgetData(args))
    // UnMount
    return () => {
      dispatch(actions.removeCallOf(token))
      cancelRequest()
      // Reset error counter to make sure it wil be restarded
      dispatch(actions.resetCall())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    locale,
    widgetId,
    widgetPreview,
    user,
    subscriptionsParamsStr,
    realBodyOptStr
  ])

  return {
    widgetPreview,
    loading,
    data,
    error
  }
}

export default useWidgetData
